import * as React from 'react';

import {
  Expressions,
  TypeImplementation,
  withMeta,
  matchExpression,
  Variable,
} from '@packages/systems/wfdl';

import {
  identifier,
  namesEqual,
  pluginName,
  qualify,
} from '@packages/systems/wfdl-Qualified';
import {weakMemo} from '@packages/utilities/memo';
import {ExpressionEditor} from '@packages/systems/ExpressionEditor-utils';
import {type Expression, TText} from '@packages/systems/wfdl-ast';
import {slugifyLive} from '@packages/systems/core/utils/RecordUtils';
import {LinkSmall} from '@packages/systems/ui/icons';
import {rightSidebarWidth} from '@packages/systems/design-system/constants/DesignerUI';
import {
  styled,
  styledDiv,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import OneLiner from '@packages/systems/design-system/OneLiner';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import {Copy} from '@packages/systems/design-system/Text';

import {constantFalse} from '@packages/utilities/fp/utils';

import {stripAll} from '@packages/utilities/contextual-expression';

const PillWrapper = styledDiv(({theme: {gutters}}) => ({
  marginTop: gutters.x8,
  width: rightSidebarWidth - gutters.x12,
}));

const Pill = emotionStyled('div')({
  boxShadow: `0 0 0 1px #333333`,
  padding: '3px 9px',
  borderRadius: tokens.controls.borderRadius,
  userSelect: 'text',
  cursor: 'text',
  verticalAlign: 'bottom',
});

const TextWrapper = styledDiv(({theme: {gutters, colors}}) => ({
  marginLeft: gutters.x16,
  display: 'flex',
  color: colors.foregroundSlightlyDim,
}));

const StyledLinkIcon = styled(LinkSmall)({
  position: 'absolute',
});

const StyledOneLiner = styled(OneLiner)({
  display: 'flex',
  alignItems: 'center',
});

const StyledURLLabel = styled(Copy)(({theme}) => ({
  marginBottom: theme.gutters.x4,
}));

const SlugPill = styled('span')(({theme: {colors, gutters, lineHeights}}) => ({
  borderRadius: 10,
  background: colors.backgroundDeepAlt2,
  paddingLeft: gutters.x4,
  paddingRight: gutters.x4,
  height: lineHeights.copy,
  color: colors.foreground,
}));

/**
 * Slug field of a CMS item
 */
export const SlugType = TypeImplementation.Alias({
  type: withMeta({placeholder: 'Enter slug...'}, TText),
  render: function Slug({expr, dispatch}) {
    const slugs = React.useMemo(
      () =>
        exprToSlugTokens(stripAll(expr)).reduce<Array<any>>(
          (result, slugToken, i, slugTexts) => {
            const {type, value} = slugToken;

            if (type === 'text') {
              result.push(<span>{value}</span>);
            } else {
              result.push(<SlugPill>{value}</SlugPill>);
            }

            if (i < slugTexts.length - 1) {
              result.push(<span>-</span>);
            }

            return result;
          },
          []
        ),
      [expr]
    );

    const slugContent = (
      <div>
        <span>test.webflow.io/</span>
        {slugs}
      </div>
    );

    return (
      <>
        <ExpressionEditor expression={expr} dispatch={dispatch} />

        <PillWrapper>
          <StyledURLLabel>URL</StyledURLLabel>
          <Pill>
            <TooltipTrigger alternate content={slugContent} placement="bottom">
              <StyledOneLiner>
                <StyledLinkIcon />
                <TextWrapper>{slugContent}</TextWrapper>
              </StyledOneLiner>
            </TooltipTrigger>
          </Pill>
        </PillWrapper>
      </>
    );
  },
});

const appendVarName = qualify(pluginName('Text'), identifier('Append'));

const isAppendVariable = matchExpression<boolean>({
  Variable: Variable.match({
    qualified: (name) => namesEqual(name, appendVarName),
    unqualified: constantFalse,
  }),
  _: constantFalse,
});

const deconstructAppend = (expr: Expression): Expression[] => {
  const {fun, args} = Expressions.deconstructCall(expr);
  if (!isAppendVariable(fun) || args.length !== 2) return [expr];
  // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'Expression'.
  const rest = deconstructAppend(args[1]);
  // @ts-expect-error - TS2322 - Type '(Expression | undefined)[]' is not assignable to type 'Expression[]'.
  return [args[0]].concat(rest);
};

const exprToSlugTokens = weakMemo(function exprToSlugTokens(expr: Expression) {
  const result: Array<
    | any
    | {
        type: string;
        value: any;
      }
    | {
        type: string;
        value: string;
      }
  > = [];
  for (const tokenExpr of deconstructAppend(expr)) {
    const slugToken = mapSlugToken(tokenExpr);
    if (slugToken) result.push(slugToken);
  }
  return result;
});

const mapSlugToken: (arg1: Expression) => {type: string; value: string} | null =
  matchExpression<{type: string; value: string} | null>({
    Call: ({arg}) => mapSlugToken(arg),
    Select: ({prop}) => ({
      type: 'variable',
      value: prop,
    }),
    Text: (text) => {
      const value = text.trim();
      if (value === '') return null;

      return {
        type: 'text',
        value: slugifyLive(value),
      };
    },
    Variable: (variable) => ({
      type: 'variable',
      value: slugifyLive(Variable.getName(variable).trim()),
    }),
    _: () => null,
  });
